import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../../components/Header';
import Load from '../../../components/Load';
import { PageBreadcrumb } from '../../../components/navigation';
import { useProfile } from '../../../context/ProfileContext';
import { useLigaPermissions } from '../../../hooks/useLigaPermissions';
import { useCategoriaLiga } from '../../../hooks/useCategoriaLiga';
import CategoriaLigaForm from '../../../components/liga/categoriaLiga/CategoriaLigaForm';
import {
  CategoriaLigaFormValues,
  toCategoriaLigaInput,
} from '../../../schemas/categoriaLiga/categoriaLigaSchema';
import type { CategoriaLiga } from '../../../types/categoriaLiga';

const emptyFormValues: CategoriaLigaFormValues = {
  nome: '',
  descricao: '',
  genero: 'MISTO',
  // AC-2: nivel é obrigatório em runtime (Zod barra o submit sem valor) — o valor
  // inicial em branco força o organizador a escolher antes de salvar.
  nivel: undefined,
  combinaveisIds: [],
  numeroAtletas: 1,
  precoInscricao: 0,
  modeloDisputa: undefined,
  tipoPontuacao: undefined,
  numeroSets: undefined,
  gamesPorSets: undefined,
  tiebreaks: undefined,
  quantidadeGrupos: undefined,
  classificadosPorGrupo: undefined,
  tamanhoGrupo: undefined,
  idadeInicial: undefined,
  idadeFinal: undefined,
  idadeCombinada: undefined,
};

const toFormValues = (categoria: CategoriaLiga): CategoriaLigaFormValues => ({
  nome: categoria.nome || '',
  descricao: categoria.descricao || '',
  genero: categoria.genero || 'MISTO',
  // AC-3: CL legada com `nivel` nulo vira `undefined` no form — mesmo schema exige a
  // escolha antes de salvar, tanto na criação quanto na edição.
  nivel: categoria.nivel ?? undefined,
  combinaveisIds: (categoria.combinaveis ?? []).map((c) => c.id),
  numeroAtletas: categoria.numeroAtletas ?? 1,
  precoInscricao: categoria.precoInscricao ?? 0,
  modeloDisputa: categoria.modeloDisputa || undefined,
  tipoPontuacao: (categoria.tipoPontuacao as CategoriaLigaFormValues['tipoPontuacao']) || undefined,
  numeroSets: categoria.numeroSets ?? undefined,
  gamesPorSets: categoria.gamesPorSets ?? undefined,
  tiebreaks: categoria.tiebreaks ?? undefined,
  quantidadeGrupos: categoria.quantidadeGrupos ?? undefined,
  classificadosPorGrupo: categoria.classificadosPorGrupo ?? undefined,
  tamanhoGrupo: categoria.tamanhoGrupo ?? undefined,
  idadeInicial: categoria.idadeInicial ?? undefined,
  idadeFinal: categoria.idadeFinal ?? undefined,
  idadeCombinada: categoria.idadeCombinada ?? undefined,
});

/**
 * Página dedicada de criação/edição de CategoriaLiga — mirror estrutural de
 * `LeagueEditCategoryTournament` (page carrega via hook/service; o form recebe por
 * props e navega de volta ao catálogo no sucesso/cancelamento).
 *
 * Rotas: `/liga/categorias/nova` (criação) e `/liga/categorias/:id/editar` (edição).
 */
const LeagueCategoriaLigaForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const { currentProfile, isLoading: profileLoading } = useProfile();
  const ligaId = currentProfile?.tipo === 'LIGA' ? currentProfile.referenciaId : null;
  const { canManageTournaments, isLoading: permissionsLoading } = useLigaPermissions(ligaId);
  const { buscarPorId, criar, atualizar, listar } = useCategoriaLiga();

  const [categoria, setCategoria] = useState<CategoriaLiga | null>(null);
  const [outrasCategorias, setOutrasCategorias] = useState<CategoriaLiga[]>([]);
  const [loading, setLoading] = useState<boolean>(isEdit);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await buscarPorId(id);
      setCategoria(data);
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar categoria da liga');
    } finally {
      setLoading(false);
    }
  }, [id, buscarPorId]);

  useEffect(() => {
    load();
  }, [load]);

  // AC-16: "outras categorias" da liga para a seção de combináveis — a própria CL em
  // edição é sempre filtrada aqui (responsabilidade da PÁGINA, nunca do CategoriaLigaForm).
  useEffect(() => {
    if (!ligaId) {
      setOutrasCategorias([]);
      return;
    }
    let cancelado = false;
    (async () => {
      try {
        const todas = (await listar(ligaId)) ?? [];
        if (!cancelado) {
          setOutrasCategorias(todas.filter((c) => c.id !== id));
        }
      } catch (e: any) {
        if (!cancelado) {
          setError(e?.message || 'Erro ao carregar categorias da liga');
        }
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [ligaId, id, listar]);

  const handleCancel = useCallback(() => {
    navigate('/liga/categorias');
  }, [navigate]);

  const handleSalvar = async (values: CategoriaLigaFormValues) => {
    if (!ligaId) return;
    const input = toCategoriaLigaInput(values, ligaId);
    try {
      if (isEdit && id) {
        await atualizar(id, input);
      } else {
        await criar(input);
      }
      navigate('/liga/categorias');
    } catch (e: any) {
      setError(e?.message || 'Erro ao salvar categoria da liga');
    }
  };

  const cardShell = (content: React.ReactNode) => (
    <>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">
            <div className="row feed-body">
              <div className="col-xl-12">
                <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                  <div className="card-body p-4">{content}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  if (profileLoading || permissionsLoading) {
    return cardShell(<Load />);
  }

  if (!currentProfile || currentProfile.tipo !== 'LIGA') {
    return null;
  }

  if (!canManageTournaments) {
    return cardShell(
      <>
        <div className="alert alert-warning" role="alert">
          <h5 className="alert-heading">Acesso Negado</h5>
          <p className="mb-0">
            Você não tem permissão para gerenciar categorias desta Liga. Apenas
            administradores e gerentes podem realizar esta ação.
          </p>
        </div>
        <button className="btn btn-primary mt-3" onClick={() => navigate('/liga/categorias')}>
          Voltar às Categorias
        </button>
      </>,
    );
  }

  return cardShell(
    <>
      <div className="mb-3">
        <PageBreadcrumb
          items={[
            { label: 'Dashboard', href: '/liga/dashboard' },
            { label: 'Categorias', href: '/liga/categorias' },
            { label: isEdit ? 'Editar Categoria' : 'Nova Categoria' },
          ]}
          onBack={handleCancel}
          variant="bootstrap"
        />
      </div>

      <h3 className="fw-700 mb-4">
        {isEdit ? 'Editar Categoria da Liga' : 'Nova Categoria da Liga'}
      </h3>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <Load />
      ) : (
        <CategoriaLigaForm
          initialValues={isEdit && categoria ? toFormValues(categoria) : emptyFormValues}
          onSubmit={handleSalvar}
          onCancel={handleCancel}
          outrasCategorias={outrasCategorias}
        />
      )}
    </>,
  );
};

export default LeagueCategoriaLigaForm;
